import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Badge } from "../ui/badge";
import {
  FilePlus2,
  Pencil,
  Send,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Clock,
  Paperclip,
  FileText,
  Download,
  Coins,
} from "lucide-react";
import { STATUS_LABELS, fmtEUR } from "./approvalsConstants";
import { safeDownloadBlob } from "../../lib/downloadUtils";
import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_META = {
  created: { icon: FilePlus2, color: "bg-slate-500", label: "Ficha creada" },
  updated: { icon: Pencil, color: "bg-blue-500", label: "Ficha actualizada" },
  sent_for_signature: { icon: Send, color: "bg-amber-500", label: "Enviada para firma" },
  approved: { icon: CheckCircle2, color: "bg-green-600", label: "Aprobada" },
  rejected: { icon: XCircle, color: "bg-red-600", label: "Rechazada" },
  resubmitted: { icon: RotateCcw, color: "bg-purple-600", label: "Reenviada (nueva versión)" },
  amounts_corrected: { icon: Coins, color: "bg-teal-600", label: "Importes corregidos" },
};

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function ApprovalDetailDialog({ open, onOpenChange, approval, token }) {
  const [attachedDocs, setAttachedDocs] = useState([]);
  const ids = approval?.attached_document_ids || [];

  useEffect(() => {
    if (!open || !ids.length || !token) {
      setAttachedDocs([]);
      return;
    }
    fetch(`${API}/api/documents`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((all) => setAttachedDocs(all.filter((d) => ids.includes(d.id))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, approval?.id, ids.length, token]);

  if (!approval) return null;
  const info = STATUS_LABELS[approval.status] || STATUS_LABELS.draft;
  const history = (approval.history || []).slice().reverse();

  const downloadDoc = async (doc) => {
    try {
      await safeDownloadBlob(`${API}/api/documents/${doc.id}/download`, doc.filename, token);
    } catch (_e) { /* noop */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="approval-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {approval.project_name}
            <Badge variant="outline" className={info.color}>{info.label}</Badge>
            <Badge variant="outline" className="bg-slate-50">v{approval.version || 1}</Badge>
          </DialogTitle>
          <DialogDescription className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <span><strong>Presupuesto:</strong> {fmtEUR(approval.presupuesto_total)}</span>
            <span><strong>Duración:</strong> {approval.duration_months || "—"} meses</span>
            <span><strong>Técnico:</strong> {approval.technician?.name || "—"}</span>
          </DialogDescription>
        </DialogHeader>

        {approval.objective && (
          <div className="rounded-lg border bg-muted/40 p-3 text-sm italic">
            "{approval.objective}"
          </div>
        )}

        {/* Decision notes when rejected */}
        {approval.status === "rejected" && approval.decision_notes && (
          <div className="rounded-lg border-2 border-red-200 bg-red-50 p-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-red-700 mb-1">
              Motivo del rechazo
            </div>
            <div className="text-sm text-red-900">{approval.decision_notes}</div>
          </div>
        )}

        {/* Attached documents */}
        {attachedDocs.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Paperclip className="w-4 h-4 text-muted-foreground" />
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Documentos adjuntos del repositorio ({attachedDocs.length})
              </h4>
            </div>
            <div className="border rounded-lg divide-y bg-muted/20">
              {attachedDocs.map((d) => (
                <div key={d.id} className="flex items-center gap-2 p-2 text-xs" data-testid={`detail-attached-${d.id}`}>
                  <FileText className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{d.title}</div>
                    <div className="text-[10px] text-muted-foreground">{d.category_label}</div>
                  </div>
                  <button
                    onClick={() => downloadDoc(d)}
                    className="p-1 hover:bg-background rounded"
                    title="Descargar"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Historial de la ficha
            </h4>
          </div>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin eventos registrados.</p>
          ) : (
            <ol className="relative border-l-2 border-slate-200 pl-6 space-y-4">
              {history.map((ev, idx) => {
                const meta = EVENT_META[ev.type] || EVENT_META.updated;
                const Icon = meta.icon;
                return (
                  <li key={idx} className="relative" data-testid={`history-event-${ev.type}`}>
                    <span
                      className={`absolute -left-[31px] top-0.5 flex w-6 h-6 rounded-full text-white items-center justify-center ${meta.color}`}
                    >
                      <Icon className="w-3 h-3" />
                    </span>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-medium text-sm">{meta.label}</span>
                      <span className="text-[11px] text-muted-foreground">
                        {formatDateTime(ev.timestamp)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      por <strong>{ev.actor_name}</strong>
                      {ev.actor_email && <span className="opacity-60"> · {ev.actor_email}</span>}
                      {ev.version && (
                        <Badge variant="outline" className="ml-2 text-[9px] py-0 px-1.5">
                          v{ev.version}
                        </Badge>
                      )}
                      {ev.with_signature && (
                        <Badge variant="outline" className="ml-2 text-[9px] py-0 px-1.5 border-primary text-primary">
                          + firma
                        </Badge>
                      )}
                    </div>
                    {ev.message && (
                      <div className="text-sm mt-1 text-foreground/80">{ev.message}</div>
                    )}
                    {ev.type === "amounts_corrected" && ev.old && ev.new && (
                      <div className="text-xs mt-1 text-teal-800 bg-teal-50 border border-teal-200 rounded px-2 py-1 inline-block" data-testid={`history-amounts-${idx}`}>
                        Presupuesto: {fmtEUR(ev.old.presupuesto_total)} → {fmtEUR(ev.new.presupuesto_total)}
                        {" · "}Subvención: {fmtEUR(ev.old.subvencion)} → {fmtEUR(ev.new.subvencion)}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
